import React, { useState, useEffect } from 'react';

function WhyChooseUs() {
  const [counters, setCounters] = useState({
    experience: 0,
    countries: 0,
    clients: 0,
    shipments: 0
  });

  const [hasScrolledToStats, setHasScrolledToStats] = useState(false);

  // Target values for counters
  const targetCounters = {
    experience: 2,
    countries: 50,
    clients: 100,
    shipments: 479
  };

  // Animate counters when section is in view
  useEffect(() => {
    const handleScroll = () => {
      const statsSection = document.getElementById('stats-section');
      if (statsSection && !hasScrolledToStats) {
        const rect = statsSection.getBoundingClientRect();
        const isVisible = rect.top < window.innerHeight && rect.bottom >= 0;
        
        if (isVisible) {
          setHasScrolledToStats(true);
          animateCounters();
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [hasScrolledToStats]);

  const animateCounters = () => {
    const duration = 2000; // Animation duration in ms
    const frameDuration = 1000 / 60; // ~60fps
    const totalFrames = Math.round(duration / frameDuration);
    
    const experienceCounter = { count: 0 };
    const countriesCounter = { count: 0 };
    const clientsCounter = { count: 0 };
    const shipmentsCounter = { count: 0 };
    
    const experienceEaseOut = (t) => 1 - Math.pow(1 - t, 3);
    const countriesEaseOut = (t) => 1 - Math.pow(1 - t, 2);
    const clientsEaseOut = (t) => 1 - Math.pow(1 - t, 2.5);
    const shipmentsEaseOut = (t) => 1 - Math.pow(1 - t, 1.5);
    
    const frame = () => {
      if (experienceCounter.count < totalFrames) {
        const progress = experienceEaseOut(experienceCounter.count / totalFrames);
        setCounters(prev => ({
          ...prev,
          experience: Math.round(progress * targetCounters.experience)
        }));
        experienceCounter.count++;
      }
      
      if (countriesCounter.count < totalFrames) {
        const progress = countriesEaseOut(countriesCounter.count / totalFrames);
        setCounters(prev => ({
          ...prev,
          countries: Math.round(progress * targetCounters.countries)
        }));
        countriesCounter.count++;
      }
      
      if (clientsCounter.count < totalFrames) {
        const progress = clientsEaseOut(clientsCounter.count / totalFrames);
        setCounters(prev => ({
          ...prev,
          clients: Math.round(progress * targetCounters.clients)
        }));
        clientsCounter.count++;
      }
      
      if (shipmentsCounter.count < totalFrames) {
        const progress = shipmentsEaseOut(shipmentsCounter.count / totalFrames);
        setCounters(prev => ({
          ...prev,
          shipments: Math.round(progress * targetCounters.shipments)
        }));
        shipmentsCounter.count++;
      }
      
      if (experienceCounter.count < totalFrames || 
          countriesCounter.count < totalFrames || 
          clientsCounter.count < totalFrames || 
          shipmentsCounter.count < totalFrames) {
        requestAnimationFrame(frame);
      }
    };
    
    requestAnimationFrame(frame);
  };

  const reasons = [
    {
      title: 'Global Reach',
      description: 'Operations in key locations worldwide.',
      details: ['Presence in major logistics hubs', 'Strategic partnerships across continents', 'Local expertise in international markets', '24/7 global operations support'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: 'Tailored Solutions',
      description: 'Customized logistics strategies to meet client needs.',
      details: ['Personalized service approach', 'Flexible shipping options', 'Custom packaging solutions', 'Industry-specific expertise'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      )
    },
    {
      title: 'Experienced Team',
      description: 'Skilled professionals with industry expertise.',
      details: ['Certified logistics specialists', 'Decades of combined experience', 'Continuous training and development', 'Multilingual support team'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-1a1.5 1.5 0 01-3 0V5.857a1.5 1.5 0 013 0V13.5z" />
        </svg>
      )
    },
    {
      title: 'Advanced Technology',
      description: 'Utilization of cutting-edge logistics technology.',
      details: ['Real-time shipment tracking', 'Automated reporting systems', 'Digital documentation', 'AI-powered route optimization'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      )
    },
    {
      title: 'Customer-Centric Approach',
      description: 'Focus on client satisfaction and service excellence.',
      details: ['Dedicated account managers', '24/7 customer support', 'Proactive communication', 'Continuous feedback integration'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
        </svg>
      )
    },
    {
      title: 'Sustainability Initiatives',
      description: 'Commitment to environmentally responsible practices.',
      details: ['Carbon-neutral shipping options', 'Eco-friendly packaging materials', 'Green transportation methods', 'Waste reduction programs'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
        </svg>
      )
    },
    {
      title: 'Efficiency',
      description: 'Streamlined processes for timely delivery.',
      details: ['Optimized routing systems', 'Fast customs clearance', 'Same-day processing options', 'Real-time delivery updates'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: 'Reliability',
      description: 'Consistent and dependable service.',
      details: ['On-time delivery guarantee', 'Quality assurance protocols', 'Comprehensive insurance coverage', 'Proven track record'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      )
    },
    {
      title: 'Cost-Effectiveness',
      description: 'Competitive pricing without compromising quality.',
      details: ['Transparent pricing structure', 'Volume-based discounts', 'No hidden fees', 'Value-added services included'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    {
      title: 'Flexibility',
      description: 'Adaptable solutions to meet diverse client needs.',
      details: ['Multiple shipping options', 'Scalable service packages', 'Customizable delivery schedules', 'Special handling capabilities'],
      icon: (
        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      )
    }
  ];

  const stats = [
    { label: 'Years of Experience', value: counters.experience, suffix: '+' },
    { label: 'Countries Served', value: counters.countries, suffix: '+' },
    { label: 'Satisfied Clients', value: counters.clients, suffix: '+' },
    { label: 'Shipments Delivered', value: counters.shipments, suffix: '+' }
  ];

  const testimonials = [
    {
      quote: "Our business depends on timely delivery of solar panels, and Stargate did not let us down. Their transparent communication and hassle-free clearance process make imports so much easier.",
      author: "Mr.Perera",
    },
    {
      quote: "From customs to final delivery, everything was smooth. At Stargate our vehicle shipments are handled with great care, and we were updated throughout the journey. Thank you for your reliable service!",
      author: "Mr.Weerasinghe",
    },
    {
      quote: "What impressed us most was how smoothly Stargate managed our heavy machinery shipments. Instead of delays and complications, everything arrived safely and right on time, saving us both money and effort.",
      author: "Mr.Ranasinghe",

    }
  ];

  return (
    <section id="why-choose-us" className="section-padding bg-black">
      <div className="container-custom">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Why Choose <span className="text-red">StarGate</span>?
          </h2>
          <p className="text-xl text-gray-light max-w-3xl mx-auto">
            At StarGate Shipping, we combine global expertise, innovative solutions, and a customer-centric approach to deliver exceptional logistics services.
          </p>
        </div>

        {/* Reasons grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-20">
          {reasons.map((reason, index) => (
            <div key={index} className="bg-ash rounded-xl p-8 border border-red/20 hover:border-red/40 transition-all duration-300 group hover:transform hover:scale-105">
              {/* Icon */}
              <div className="w-16 h-16 bg-gradient-to-r from-red to-orange rounded-lg flex items-center justify-center mb-6 group-hover:shadow-lg group-hover:shadow-red/25 transition-all duration-300">
                <div className="text-white">{reason.icon}</div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold mb-4 text-white group-hover:text-red transition-colors">
                {reason.title}
              </h3>

              <p className="text-gray-light mb-6 leading-relaxed">
                {reason.description}
              </p>

              {/* Details */}
              <ul className="space-y-2">
                {reason.details.slice(0, 4).map((detail, detailIndex) => (
                  <li key={detailIndex} className="flex items-center text-sm text-gray-light">
                    <div className="w-1.5 h-1.5 bg-red rounded-full mr-3"></div>
                    {detail}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Stats section */}
        <div id="stats-section" className="bg-gradient-to-r from-red to-orange rounded-2xl p-8 md:p-12 mb-20">
          <div className="text-center mb-8">
            <h3 className="text-3xl font-bold text-white mb-4">
              Trusted by Businesses Worldwide
            </h3>
            <p className="text-gray-light text-lg">
              Our track record speaks for itself
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-white mb-2">
                  {stat.value}{stat.suffix}
                </div>
                <div className="text-gray-light text-sm md:text-base">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Testimonials */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center mb-12">
            What Our <span className="text-red">Clients Say</span>
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-ash rounded-xl p-6 border border-red/20 relative">

                
                <div className="mb-4">
                  <svg className="w-8 h-8 text-red mb-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v12a1 1 0 01-1 1H4a1 1 0 01-1-1V4zm1 1v10h12V5H4z" clipRule="evenodd" />
                    <path d="M6 7h8v2H6V7zM6 11h8v2H6v-2z" />
                  </svg>
                </div>

                <blockquote className="text-gray-light mb-6 leading-relaxed">
                  "{testimonial.quote}"
                </blockquote>

                <div className="border-t border-gray-700 pt-4">
                  <div className="text-white font-semibold">{testimonial.author}</div>
                  <div className="text-red text-sm">{testimonial.role}</div>
                  <div className="text-gray-light text-sm">{testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Call to action */}
        <div className="text-center">
          <div className="bg-ash rounded-2xl p-8 md:p-12 border border-red/20">
            <h3 className="text-3xl font-bold mb-6">
              Ready to Experience the <span className="text-red">StarGate</span> Difference?
            </h3>
            <p className="text-xl text-gray-light mb-8 max-w-2xl mx-auto">
              Join hundred of satisfied customers who trust StarGate for their 
              international logistics needs.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  const contactSection = document.getElementById('contact');
                  if (contactSection) {
                    contactSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="btn-primary text-lg px-8 py-4"
              >
                Get Started Today
              </button>
              <button
                onClick={() => {
                  const servicesSection = document.getElementById('services');
                  if (servicesSection) {
                    servicesSection.scrollIntoView({ behavior: 'smooth' });
                  }
                }}
                className="btn-secondary text-lg px-8 py-4"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default WhyChooseUs;